/**
 * Overview Loader Component
 * Placeholder loader that matches the overview data structure
 */

import React from 'react';
import { spacing, borders, backgroundColors } from '@/design-system/utils';

export const OverviewLoader: React.FC = () => {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[3],
      }}
    >
      {/* Overview Intro Section Loader */}
      <div
        style={{
          backgroundColor: backgroundColors.primary,
          borderRadius: borders.radius.lg,
          padding: spacing[4],
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[3],
          alignItems: 'flex-end',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[2],
            width: '100%',
          }}
        >
          {/* Title loader */}
          <div
            style={{
              display: 'flex',
              gap: 6,
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: 20,
                height: 20,
                backgroundColor: '#E7E5E4',
                borderRadius: '4px',
                flexShrink: 0,
              }}
              className='animate-pulse'
            />
            <div
              style={{
                width: '200px',
                height: '24px',
                backgroundColor: '#E7E5E4',
                borderRadius: '4px',
              }}
              className='animate-pulse'
            />
          </div>
          {/* Description loader */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing[1],
            }}
          >
            <div
              style={{
                width: '100%',
                height: '16px',
                backgroundColor: '#E7E5E4',
                borderRadius: '4px',
              }}
              className='animate-pulse'
            />
            <div
              style={{
                width: '90%',
                height: '16px',
                backgroundColor: '#E7E5E4',
                borderRadius: '4px',
              }}
              className='animate-pulse'
            />
            <div
              style={{
                width: '85%',
                height: '16px',
                backgroundColor: '#E7E5E4',
                borderRadius: '4px',
              }}
              className='animate-pulse'
            />
          </div>
        </div>
        {/* Show more button loader */}
        <div
          style={{
            width: '100px',
            height: '32px',
            backgroundColor: '#E7E5E4',
            borderRadius: '4px',
          }}
          className='animate-pulse'
        />
      </div>

      {/* Destination Overview Section Loader */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[3],
        }}
      >
        {/* Section header loader */}
        <div
          style={{
            width: '180px',
            height: '24px',
            backgroundColor: '#E7E5E4',
            borderRadius: '4px',
          }}
          className='animate-pulse'
        />
        {/* Grid of stat cards loader */}
        <div
          style={{
            gap: spacing[2],
            padding: `0 ${spacing[3]}px ${spacing[3]}px`,
            borderRadius: borders.radius['2xl'],
            width: '100%',
          }}
          className='grid grid-cols-2 md:grid-cols-3'
        >
          {Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing[1],
                padding: spacing[3],
                backgroundColor: backgroundColors.primary,
                borderRadius: borders.radius.lg,
                minHeight: '100px',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#E7E5E4',
                  borderRadius: '8px',
                }}
                className='animate-pulse'
              />
              <div
                style={{
                  width: '80px',
                  height: '14px',
                  backgroundColor: '#E7E5E4',
                  borderRadius: '4px',
                }}
                className='animate-pulse'
              />
              <div
                style={{
                  width: '60px',
                  height: '16px',
                  backgroundColor: '#E7E5E4',
                  borderRadius: '4px',
                }}
                className='animate-pulse'
              />
            </div>
          ))}
        </div>
      </div>

      {/* Emergency Contacts Section Loader */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[3],
        }}
      >
        {/* Section header loader */}
        <div
          style={{
            width: '160px',
            height: '24px',
            backgroundColor: '#E7E5E4',
            borderRadius: '4px',
          }}
          className='animate-pulse'
        />
        {/* Contact cards loader */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: spacing[2],
            padding: `0 ${spacing[3]}px ${spacing[3]}px`,
            borderRadius: borders.radius['2xl'],
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: spacing[2],
                padding: spacing[3],
                backgroundColor: backgroundColors.primary,
                borderRadius: borders.radius.lg,
                alignItems: 'center',
                minHeight: '120px',
              }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#E7E5E4',
                  borderRadius: '50%',
                }}
                className='animate-pulse'
              />
              <div
                style={{
                  width: '100px',
                  height: '14px',
                  backgroundColor: '#E7E5E4',
                  borderRadius: '4px',
                }}
                className='animate-pulse'
              />
              <div
                style={{
                  width: '80px',
                  height: '16px',
                  backgroundColor: '#E7E5E4',
                  borderRadius: '4px',
                }}
                className='animate-pulse'
              />
            </div>
          ))}
        </div>
      </div>

      {/* Festivals & Celebrations Section Loader */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[3],
        }}
      >
        {/* Section header loader */}
        <div
          style={{
            width: '220px',
            height: '24px',
            backgroundColor: '#E7E5E4',
            borderRadius: '4px',
          }}
          className='animate-pulse'
        />
        {/* Festival cards loader */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[2],
          }}
        >
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing[2],
                padding: spacing[3],
                backgroundColor: backgroundColors.primary,
                borderRadius: borders.radius.lg,
              }}
            >
              {/* Festival image loader */}
              <div
                style={{
                  width: '100%',
                  height: '200px',
                  backgroundColor: '#E7E5E4',
                  borderRadius: borders.radius.md,
                }}
                className='animate-pulse'
              />
              {/* Festival title loader */}
              <div
                style={{
                  width: '60%',
                  height: '20px',
                  backgroundColor: '#E7E5E4',
                  borderRadius: '4px',
                }}
                className='animate-pulse'
              />
              {/* Festival description loader */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing[1],
                }}
              >
                <div
                  style={{
                    width: '100%',
                    height: '14px',
                    backgroundColor: '#E7E5E4',
                    borderRadius: '4px',
                  }}
                  className='animate-pulse'
                />
                <div
                  style={{
                    width: '90%',
                    height: '14px',
                    backgroundColor: '#E7E5E4',
                    borderRadius: '4px',
                  }}
                  className='animate-pulse'
                />
                <div
                  style={{
                    width: '75%',
                    height: '14px',
                    backgroundColor: '#E7E5E4',
                    borderRadius: '4px',
                  }}
                  className='animate-pulse'
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
